import { useState, useRef } from "react";
import { ChevronDown, ChevronUp, Play, Square, Pause, Mic, CheckCircle2, AlertTriangle, Image, MessageSquare, Send } from "lucide-react";
import { useMotion } from "@/lib/safeMotion";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface IncidentCardData {
  id: string;
  title: string;
  description: string;
  severity: string;
  category: string;
  time: string;
  date: string;
  officer: string;
  site: string;
  site_id: string | null;
  escalated: boolean;
  resolved: boolean;
  hasVoiceNote: boolean;
  voiceDuration: number;
  audio_url?: string | null;
  photo_url?: string | null;
  ai_analysis?: any;
  resolution_notes?: string | null;
}

interface IncidentCardProps {
  incident: IncidentCardData;
  onResolve?: (id: string, notes: string) => Promise<void>;
  resolving?: boolean;
  canResolve?: boolean;
}

const IncidentCard = ({ incident: inc, onResolve, resolving, canResolve = true }: IncidentCardProps) => {
  const { motion } = useMotion();
  const [expanded, setExpanded] = useState(false);
  const [playingVoice, setPlayingVoice] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState(inc.resolution_notes || "");
  const [photoOpen, setPhotoOpen] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const handlePlayVoice = () => {
    if (!inc.audio_url) return;
    if (playingVoice) {
      audioRef.current?.pause();
      setPlayingVoice(false);
    } else {
      if (!audioRef.current) {
        audioRef.current = new Audio(inc.audio_url);
        audioRef.current.onended = () => setPlayingVoice(false);
      }
      audioRef.current.play().catch(() => {
        toast.error("Unable to play voice note");
        setPlayingVoice(false);
      });
      setPlayingVoice(true);
    }
  };

  const severityColor = inc.severity.toLowerCase() === "high" || inc.severity.toLowerCase() === "critical"
    ? "app-red" : inc.severity.toLowerCase() === "medium" ? "app-gold" : "app-blue";

  return (
    <>
      <div className={`app-card rounded-xl border-l-4 overflow-hidden ${
        severityColor === "app-red" ? "border-l-[hsl(var(--app-red))]" :
        severityColor === "app-gold" ? "border-l-[hsl(var(--app-gold))]" :
        "border-l-[hsl(var(--app-blue))]"
      } ${inc.escalated ? "ring-1 ring-[hsl(var(--app-red)/0.5)]" : ""}`}>
        {/* Summary row — always visible */}
        <button
          onClick={() => setExpanded(e => !e)}
          className="w-full p-4 flex items-start gap-3 text-left hover:bg-[hsl(var(--app-surface-2)/0.5)] transition-colors"
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[hsl(var(--${severityColor})/0.15)] text-[hsl(var(--${severityColor}))]`}>
                {inc.severity}
              </span>
              <span className="px-2 py-0.5 rounded-full text-[10px] bg-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text-secondary))]">{inc.category}</span>
              {inc.escalated && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[hsl(var(--app-red)/0.15)] text-[hsl(var(--app-red))] inline-flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> ESCALATED
                </span>
              )}
              {inc.resolved && (
                <span className="px-2 py-0.5 rounded-full text-[10px] bg-[hsl(var(--app-green)/0.15)] text-[hsl(var(--app-green))] inline-flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> Resolved
                </span>
              )}
              {inc.hasVoiceNote && <Mic className="w-3 h-3 text-[hsl(var(--app-gold))]" />}
              {inc.photo_url && <Image className="w-3 h-3 text-[hsl(var(--app-blue))]" />}
            </div>
            <p className="text-sm font-medium text-[hsl(var(--app-text-primary))] truncate">{inc.title}</p>
            <p className="text-xs text-[hsl(var(--app-text-muted))] mt-1">{inc.officer} · {inc.site} · {inc.date} {inc.time}</p>
          </div>
          <div className="shrink-0 mt-1 text-[hsl(var(--app-text-muted))]">
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </div>
        </button>

        {/* Expanded details */}
        {expanded && (
          <div className="px-4 pb-4 space-y-3 border-t border-[hsl(var(--app-border-subtle))]">
            {/* Description */}
            {inc.description && (
              <div className="pt-3">
                <p className="text-xs font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider mb-1">Description</p>
                <p className="text-sm text-[hsl(var(--app-text-secondary))]">{inc.description}</p>
              </div>
            )}

            {/* Photo */}
            {inc.photo_url && (
              <div>
                <p className="text-xs font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider mb-1.5">Photo Evidence</p>
                <button onClick={() => setPhotoOpen(true)} className="group relative">
                  <img
                    src={inc.photo_url}
                    alt="Incident photo"
                    className="w-full max-w-xs rounded-lg border border-[hsl(var(--app-border-subtle))] object-cover max-h-48 group-hover:brightness-110 transition-all"
                  />
                  <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="px-2 py-1 rounded bg-black/60 text-white text-xs">Click to enlarge</span>
                  </span>
                </button>
              </div>
            )}

            {/* Voice note */}
            {inc.hasVoiceNote && inc.audio_url && (
              <div>
                <p className="text-xs font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider mb-1.5">Voice Note</p>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-[hsl(var(--app-surface-2))] border border-[hsl(var(--app-border-subtle))]">
                  <button onClick={handlePlayVoice} className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                    playingVoice ? "bg-[hsl(var(--app-red))] text-white" : "bg-[hsl(var(--app-gold)/0.15)] text-[hsl(var(--app-gold))]"
                  }`}>
                    {playingVoice ? <Square className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5 ml-0.5" />}
                  </button>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Mic className="w-3 h-3 text-[hsl(var(--app-gold))]" />
                      <span className="text-xs font-medium text-[hsl(var(--app-text-secondary))]">Voice Note</span>
                    </div>
                    <div className="h-1 rounded-full bg-[hsl(var(--app-border-subtle))] mt-1.5 overflow-hidden">
                      <motion.div
                        className="h-full rounded-full bg-[hsl(var(--app-gold))]"
                        initial={{ width: "0%" }}
                        animate={{ width: playingVoice ? "100%" : "0%" }}
                        transition={{ duration: 10, ease: "linear" }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* AI Analysis */}
            {inc.ai_analysis && (
              <div>
                <p className="text-xs font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider mb-1.5">AI Analysis</p>
                <div className="p-3 rounded-lg bg-[hsl(var(--app-surface-2))] border border-[hsl(var(--app-border-subtle))] text-xs text-[hsl(var(--app-text-secondary))] space-y-1">
                  {typeof inc.ai_analysis === "object" ? (
                    Object.entries(inc.ai_analysis).map(([key, val]) => (
                      <p key={key}><span className="font-semibold text-[hsl(var(--app-text-primary))] capitalize">{key.replace(/_/g, " ")}:</span> {String(val)}</p>
                    ))
                  ) : (
                    <p>{String(inc.ai_analysis)}</p>
                  )}
                </div>
              </div>
            )}

            {/* Resolution notes (if already resolved) */}
            {inc.resolved && inc.resolution_notes && (
              <div>
                <p className="text-xs font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider mb-1">Resolution Notes</p>
                <div className="p-3 rounded-lg bg-[hsl(var(--app-green)/0.08)] border border-[hsl(var(--app-green)/0.2)] text-sm text-[hsl(var(--app-text-secondary))]">
                  {inc.resolution_notes}
                </div>
              </div>
            )}

            {/* Resolve action with comment */}
            {!inc.resolved && canResolve && onResolve && (
              <div className="pt-1 space-y-2">
                <p className="text-xs font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider">Resolve Incident</p>
                <textarea
                  value={resolutionNotes}
                  onChange={(e) => setResolutionNotes(e.target.value)}
                  placeholder="Describe how this was resolved…"
                  rows={2}
                  className="w-full rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-[hsl(var(--app-text-primary))] px-3 py-2 text-sm resize-none placeholder:text-[hsl(var(--app-text-muted))]"
                />
                <button
                  onClick={() => onResolve(inc.id, resolutionNotes)}
                  disabled={resolving}
                  className="px-4 py-2 rounded-lg text-xs font-medium border border-[hsl(var(--app-green)/0.3)] text-[hsl(var(--app-green))] hover:bg-[hsl(var(--app-green)/0.1)] transition-colors disabled:opacity-50 flex items-center gap-1.5"
                >
                  <CheckCircle2 className="w-3.5 h-3.5" /> {resolving ? "Resolving…" : "Mark as Resolved"}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Full-screen photo lightbox */}
      {photoOpen && inc.photo_url && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4" onClick={() => setPhotoOpen(false)}>
          <img src={inc.photo_url} alt="Incident photo" className="max-w-full max-h-full rounded-lg shadow-2xl" />
        </div>
      )}
    </>
  );
};

export default IncidentCard;
